import { Outlet, useLocation } from "@/lib/router-compat";
import { lazy, Suspense } from "react";
import { TopNav } from "./TopNav";
import { Footer } from "./Footer";
import { ScrollToTop } from "./ScrollToTop";
import { PageTransition } from "./PageTransition";
import { usePageView } from "@/hooks/usePageView";

// Lazy-load the floating chatbot. It pulls in heavy deps (dotlottie player,
// framer-motion, react-markdown) that are not needed for initial render.
// Loading it after the main page reduces main-thread JS evaluation on first paint.
const FloatingBobChatbot = lazy(() =>
  import("@/components/FloatingBobChatbot").then((m) => ({
    default: m.FloatingBobChatbot,
  })),
);

// Routes where the public-facing assistant should appear. We hide it inside
// authenticated workspaces (seller, buyer, admin, profile) where it just
// adds noise and JS weight.
const CHATBOT_PREFIXES = ["/", "/about", "/blog", "/terms", "/privacy", "/auth", "/how-it-works", "/coming-soon", "/rates", "/locations", "/insights"];

const shouldShowChatbot = (pathname: string) => {
  if (pathname.startsWith("/seller") || pathname.startsWith("/buyer") || pathname.startsWith("/admin") || pathname.startsWith("/profile")) {
    return false;
  }
  return CHATBOT_PREFIXES.some((p) => (p === "/" ? pathname === "/" : pathname.startsWith(p)));
};

export const AppLayout = () => {
  usePageView();
  const { pathname } = useLocation();
  const showChatbot = shouldShowChatbot(pathname);
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <ScrollToTop />
      <TopNav />
      <main className="flex-1">
        <PageTransition>
          <Outlet />
        </PageTransition>
      </main>
      <Footer />
      {showChatbot && (
        <Suspense fallback={null}>
          <FloatingBobChatbot />
        </Suspense>
      )}
    </div>
  );
};
